import * as THREE from "three";

/** The renderer, scene, camera and lights, plus the grid and markers drawn on the base. */
export function createScene() {
  /* =====================================================================
     RENDERING SETUP
     ===================================================================== */
  const scene = new THREE.Scene();
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(innerWidth, innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  document.body.appendChild(renderer.domElement);

  const camera = new THREE.PerspectiveCamera(50, innerWidth / innerHeight, 0.1, 300);

  const darkQuery = matchMedia("(prefers-color-scheme: dark)");
  function applySceneTheme() {
    const forced = document.documentElement.dataset.theme;
    const dark = forced ? forced === "dark" : darkQuery.matches;
    const col = new THREE.Color(dark ? 0x1b2431 : 0xcfd8e3);
    scene.background = col;
    scene.fog = new THREE.Fog(col, 60, 160);
    document.body.style.background = "#" + col.getHexString();
  }
  applySceneTheme();
  darkQuery.addEventListener("change", applySceneTheme);

  // ×π since three r155 uses physical light units; checked by eye against the r128 site
  scene.add(new THREE.HemisphereLight(0xffffff, 0x6d7480, 0.8 * Math.PI));
  const sun = new THREE.DirectionalLight(0xfff4e0, 0.9 * Math.PI);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, {
    left: -20,
    right: 20,
    top: 20,
    bottom: -20,
    near: 1,
    far: 100,
  });
  sun.shadow.bias = -0.0005;
  scene.add(sun, sun.target);

  // The physics base is invisible: only its grid lines are drawn, plus a
  // transparent surface that just catches shadows
  const shadowCatcher = new THREE.Mesh(
    new THREE.PlaneGeometry(24, 24),
    new THREE.ShadowMaterial({ opacity: 0.25 }),
  );
  shadowCatcher.rotation.x = -Math.PI / 2;
  shadowCatcher.receiveShadow = true;
  scene.add(shadowCatcher);

  // Grid lines every 1 m, matching the size of a cube
  const grid = new THREE.GridHelper(24, 24, 0x7d8899, 0x7d8899);
  grid.position.y = 0.005;
  const gridMaterial = grid.material as THREE.Material;
  gridMaterial.transparent = true;
  gridMaterial.opacity = 0.85;
  scene.add(grid);

  // Best-height marker: a gold outline around the tower
  const markerGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-12.4, 0, -12.4),
    new THREE.Vector3(12.4, 0, -12.4),
    new THREE.Vector3(12.4, 0, 12.4),
    new THREE.Vector3(-12.4, 0, 12.4),
  ]);
  const marker = new THREE.LineLoop(markerGeo, new THREE.LineBasicMaterial({ color: 0xf2bd4a }));
  marker.visible = false;
  scene.add(marker);

  addEventListener("resize", () => {
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight);
  });

  return { scene, renderer, camera, sun, marker };
}
